import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, ArrowRight, ChevronsLeftRight, Star } from 'lucide-react';
import { getProjects } from '../api/api';

// Comparateur avant/après : la poignée démarre au centre et se glisse à la
// souris ou au doigt pour révéler davantage l'image « avant » ou « après ».
const ComparateurAvantApres = ({ before, after, title, featured }) => {
  const conteneur = useRef(null);
  const glisse = useRef(false);
  const [pos, setPos] = useState(50);
  const [largeur, setLargeur] = useState(0);

  useEffect(() => {
    const mesurer = () => setLargeur(conteneur.current?.offsetWidth || 0);
    mesurer();
    window.addEventListener('resize', mesurer);
    return () => window.removeEventListener('resize', mesurer);
  }, []);

  const majDepuisX = (clientX) => {
    const el = conteneur.current;
    if (!el || clientX == null) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };

  useEffect(() => {
    const bouger = (e) => {
      if (!glisse.current) return;
      const x = e.clientX ?? e.touches?.[0]?.clientX;
      majDepuisX(x);
    };
    const relacher = () => { glisse.current = false; };
    window.addEventListener('mousemove', bouger);
    window.addEventListener('touchmove', bouger, { passive: true });
    window.addEventListener('mouseup', relacher);
    window.addEventListener('touchend', relacher);
    return () => {
      window.removeEventListener('mousemove', bouger);
      window.removeEventListener('touchmove', bouger);
      window.removeEventListener('mouseup', relacher);
      window.removeEventListener('touchend', relacher);
    };
  }, []);

  const demarrer = (e) => {
    e.stopPropagation();
    glisse.current = true;
    majDepuisX(e.clientX ?? e.touches?.[0]?.clientX);
  };

  return (
    <div
      ref={conteneur}
      className="relative h-80 overflow-hidden select-none"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Après : image de base, pleine largeur */}
      <img
        src={after}
        alt={`${title} — Après`}
        className="absolute inset-0 w-full h-full object-cover"
        draggable="false"
        loading="lazy"
      />
      <div className="absolute top-4 right-4 z-10 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
        Après
      </div>

      {/* Avant : couche rognée à la position de la poignée */}
      <div className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={before}
          alt={`${title} — Avant`}
          className="absolute inset-0 h-full object-cover max-w-none"
          style={{ width: largeur ? `${largeur}px` : '100%' }}
          draggable="false"
          loading="lazy"
        />
        <div className="absolute top-4 left-4 bg-gray-900/80 text-white px-3 py-1 rounded-full text-xs font-semibold">
          Avant
        </div>
      </div>

      {/* Poignée déplaçable */}
      <div className="absolute inset-y-0 z-20" style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}>
        <div className="relative h-full w-0.5 bg-white/90 shadow-[0_0_0_1px_rgba(0,0,0,0.08)]">
          <button
            type="button"
            onMouseDown={demarrer}
            onTouchStart={demarrer}
            aria-label="Glisser pour comparer l'avant et l'après"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-xl cursor-ew-resize transition-transform"
          >
            <ChevronsLeftRight className="w-5 h-5 text-blue-600" aria-hidden="true" />
          </button>
        </div>
      </div>

      {featured && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30">
          <Badge className="bg-blue-600 text-white font-semibold gap-1">
            <Star className="w-3.5 h-3.5" aria-hidden="true" />
            Projet vedette
          </Badge>
        </div>
      )}
    </div>
  );
};

export const Gallery = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await getProjects();
        setProjects(data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  if (loading) {
    return (
      <section id="gallery" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-gray-400">Chargement des réalisations…</div>
        </div>
      </section>
    );
  }

  if (projects.length === 0) {
    return null;
  }

  return (
    <section id="gallery" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100">
            Nos réalisations
          </Badge>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-gray-900 mb-4">
            Projets réalisés
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Glissez le curseur sur chaque photo pour comparer l'avant et l'après.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {projects.map((project) => (
            <Card
              key={project.id}
              className="overflow-hidden border-0 bg-white hover:shadow-lg transition-shadow duration-500"
            >
              <ComparateurAvantApres
                before={project.before_image}
                after={project.after_image}
                title={project.title}
                featured={project.is_featured}
              />

              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-bold text-gray-900">
                    {project.title}
                  </h3>
                  {project.location && (
                    <div className="flex items-center text-sm text-gray-500">
                      <MapPin className="w-4 h-4 mr-1" />
                      {project.location}
                    </div>
                  )}
                </div>

                {project.service_type && (
                  <Badge className="mb-3 bg-blue-50 text-blue-700 hover:bg-blue-50">
                    {project.service_type}
                  </Badge>
                )}

                <p className="text-gray-600 leading-relaxed">
                  {project.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16 p-8 bg-gray-50 rounded-3xl">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Vous avez un projet de nettoyage ?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Contactez-nous pour obtenir un devis personnalisé et découvrir comment nous pouvons transformer vos espaces.
          </p>
          <Link
            to="/devis"
            className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-600/30 transition-all duration-300"
          >
            Demander un devis gratuit
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};
